function valueFrom(source, camelName, pascalName) {
  return source?.[camelName] ?? source?.[pascalName];
}

function firstValue(source, names) {
  for (const name of names) {
    const pascalName = name.charAt(0).toUpperCase() + name.slice(1);
    const value = valueFrom(source, name, pascalName);
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function nonNegativeNumber(value, fallback = 0) {
  return Math.max(0, finiteNumber(value, fallback));
}

const PROFESSION_NAMES = {
  construction: 'Construction',
  salvage: 'Salvage',
  weaponcraft: 'Weaponcraft',
  armorcraft: 'Armorcraft',
  alchemy: 'Alchemy',
  cooking: 'Cooking',
};

const PROFESSION_DESCRIPTIONS = {
  construction: 'Refine Components at the Workbench. Higher levels unlock Construction recipes.',
  salvage: 'Dismantle equipment at the Workbench. Higher levels improve material recovery.',
  weaponcraft: 'Craft weapons at the Weapon Smithy. Higher levels unlock Weaponcraft recipes.',
  armorcraft: 'Craft armor at the Armor Workshop. Higher levels unlock Armorcraft recipes.',
  alchemy: 'Craft restorative and alchemical recipes at the Alchemy Lab. Higher levels unlock recipes.',
  cooking: 'Prepare food at the Cooking Place. Higher levels unlock recipes.',
};

function professionDisplayName(professionKey) {
  const normalizedKey = String(professionKey || '').trim();
  if (!normalizedKey) return '';
  if (PROFESSION_NAMES[normalizedKey]) {
    return PROFESSION_NAMES[normalizedKey];
  }
  return normalizedKey
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}

function professionDescription(professionKey) {
  return PROFESSION_DESCRIPTIONS[String(professionKey || '').trim()] || '';
}

function normalizeStatus(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '');

  if (['complete', 'completed', 'built', 'finished'].includes(normalized)) {
    return 'completed';
  }
  if (['building', 'constructing', 'construction', 'inprogress', 'started'].includes(normalized)) {
    return 'building';
  }
  if (['ready', 'readytobuild', 'funded', 'prepared'].includes(normalized)) {
    return 'readyToBuild';
  }
  if (['locked', 'unavailable', 'disabled'].includes(normalized)) {
    return 'locked';
  }
  return 'unbuilt';
}

export function createBaseCampOperationId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function formatBuildDuration(durationSeconds) {
  const seconds = Math.max(0, Math.floor(Number(durationSeconds) || 0));
  if (seconds <= 0) return 'Unavailable';

  const parts = [];
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }
  if (remainingSeconds > 0) {
    parts.push(
      `${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}`,
    );
  }
  return parts.join(' ');
}

function pointFrom(value) {
  const x = Number(value?.x);
  const y = Number(value?.y);
  return Number.isInteger(x) && Number.isInteger(y) ? { x, y } : null;
}

export function getCampTravelAvailability({
  currentAction,
  entrance,
  hasCombat = false,
  isInCamp = false,
  playerPosition,
}) {
  const action = String(currentAction || '').trim().toUpperCase();
  if (hasCombat) {
    return {
      allowed: false,
      distance: null,
      reason: 'Travel is unavailable during combat.',
    };
  }
  if (!['IDLE', 'RESTING'].includes(action)) {
    return {
      allowed: false,
      distance: null,
      reason: action === 'COLLECTING'
        ? 'Finish gathering before travelling to or from Camp.'
        : action === 'MOVING'
          ? 'Wait until movement finishes before travelling.'
          : 'Finish the current action before travelling.',
    };
  }

  if (isInCamp) {
    return {
      allowed: true,
      distance: 0,
      reason: 'Leave Camp to return to the settlement entrance.',
    };
  }

  const resolvedEntrance = pointFrom(entrance);
  if (!resolvedEntrance) {
    return {
      allowed: false,
      distance: null,
      reason: 'The Camp entrance location is still loading.',
    };
  }
  const resolvedPosition = pointFrom(playerPosition);
  if (!resolvedPosition) {
    return {
      allowed: false,
      distance: null,
      reason: 'Your position is still loading.',
    };
  }

  const distance = Math.abs(resolvedPosition.x - resolvedEntrance.x)
    + Math.abs(resolvedPosition.y - resolvedEntrance.y);
  if (distance <= 1) {
    return {
      allowed: true,
      distance,
      reason: 'The Camp entrance is within reach.',
    };
  }

  return {
    allowed: false,
    distance,
    reason: `Move to the Camp entrance marker (${distance} tiles away). You can enter from the marker or one adjacent tile.`,
  };
}

export function normalizeBaseCampCost(cost) {
  if (!cost) return null;
  const itemId = firstValue(cost, [
    'itemId',
    'resourceItemId',
    'materialItemId',
    'definitionKey',
  ]);
  if (!itemId) return null;

  const required = nonNegativeNumber(firstValue(cost, [
    'required',
    'requiredQuantity',
    'quantityRequired',
    'amount',
  ]));
  const contributed = Math.min(
    required || Infinity,
    nonNegativeNumber(firstValue(cost, [
      'contributed',
      'contributedQuantity',
      'quantityContributed',
      'deposited',
      'amountContributed',
    ])),
  );

  return {
    itemId: String(itemId),
    itemName: String(firstValue(cost, [
      'itemName',
      'resourceName',
      'materialName',
      'name',
    ]) || itemId),
    required,
    contributed: Number.isFinite(contributed) ? contributed : 0,
    availableInPack: nonNegativeNumber(firstValue(cost, [
      'availableInPack',
      'packAvailable',
      'inventoryAvailable',
    ])),
    availableInStorage: nonNegativeNumber(firstValue(cost, [
      'availableInStorage',
      'storageAvailable',
      'vaultAvailable',
    ])),
  };
}

export function isBuildingFunded(building) {
  const costs = Array.isArray(building?.costs) ? building.costs : [];
  return costs.length > 0
    && costs.every(cost => cost.required > 0 && cost.contributed >= cost.required);
}

export function normalizeBaseCampBuilding(building, index = 0) {
  if (!building) return null;
  const buildingKey = firstValue(building, [
    'buildingKey',
    'key',
    'definitionKey',
    'id',
  ]);
  if (!buildingKey) return null;

  const costsPayload = firstValue(building, [
    'costs',
    'requirements',
    'materials',
    'contributions',
  ]);
  const costs = (Array.isArray(costsPayload) ? costsPayload : [])
    .map(normalizeBaseCampCost)
    .filter(Boolean);
  const startedAtUtc = firstValue(building, [
    'startedAtUtc',
    'buildStartedAtUtc',
    'constructionStartedAtUtc',
  ]) || null;
  const completesAtUtc = firstValue(building, [
    'completesAtUtc',
    'buildCompletesAtUtc',
    'constructionCompletesAtUtc',
    'endsAtUtc',
  ]) || null;
  const completed = Boolean(firstValue(building, [
    'completed',
    'isCompleted',
    'built',
  ]));
  const constructing = Boolean(firstValue(building, [
    'building',
    'isBuilding',
    'underConstruction',
  ]));
  const declaredStatus = normalizeStatus(firstValue(building, [
    'status',
    'state',
  ]));
  let status = completed || declaredStatus === 'completed'
    ? 'completed'
    : constructing || declaredStatus === 'building' || completesAtUtc
      ? 'building'
      : declaredStatus;
  const currentLevel = Math.floor(nonNegativeNumber(firstValue(building, [
    'currentLevel',
    'level',
    'buildingLevel',
  ]), completed || declaredStatus === 'completed' ? 1 : 0));
  const maxLevel = Math.max(
    currentLevel,
    Math.floor(nonNegativeNumber(firstValue(building, [
      'maxLevel',
      'maximumLevel',
    ]), Math.max(1, currentLevel))),
  );
  const targetLevel = Math.min(
    maxLevel,
    Math.max(
      currentLevel,
      Math.floor(nonNegativeNumber(firstValue(building, [
        'targetLevel',
        'projectTargetLevel',
        'nextLevel',
      ]), status === 'building' ? currentLevel + 1 : currentLevel)),
    ),
  );
  const hasCraftingStation = Boolean(firstValue(building, [
    'craftingStationKey',
    'stationKey',
  ]));

  const normalized = {
    buildingKey: String(buildingKey),
    name: String(firstValue(building, [
      'name',
      'displayName',
      'buildingName',
    ]) || buildingKey),
    description: String(firstValue(building, [
      'description',
      'flavourText',
      'summary',
    ]) || ''),
    imageId: String(firstValue(building, [
      'imageId',
      'cityImageId',
    ]) || ''),
    professionKey: String(firstValue(building, [
      'professionKey',
    ]) || ''),
    craftingStationKey: String(firstValue(building, [
      'craftingStationKey',
      'stationKey',
    ]) || ''),
    currentLevel,
    targetLevel,
    maxLevel,
    recipeItemLevelCap: Math.floor(nonNegativeNumber(firstValue(building, [
      'recipeItemLevelCap',
      'maxCraftedItemLevel',
      'itemLevelCap',
    ]), hasCraftingStation ? currentLevel * 5 : 0)),
    status,
    costs,
    startedAtUtc,
    completesAtUtc,
    buildDurationSeconds: nonNegativeNumber(firstValue(building, [
      'buildDurationSeconds',
      'durationSeconds',
      'constructionSeconds',
    ])),
    lockedReason: String(firstValue(building, [
      'lockedReason',
      'disabledReason',
    ]) || ''),
    order: finiteNumber(firstValue(building, ['order', 'sortOrder']), index),
  };

  if (status === 'unbuilt' && isBuildingFunded(normalized)) {
    status = 'readyToBuild';
    normalized.status = status;
  }
  return normalized;
}

export function normalizeBaseCampStorageEntry(entry, index = 0) {
  if (!entry) return null;
  const itemId = firstValue(entry, ['itemId', 'definitionKey']);
  const quantity = nonNegativeNumber(firstValue(entry, ['quantity', 'amount']));
  if (!itemId || quantity <= 0) return null;

  return {
    entryId: String(firstValue(entry, ['entryId', 'id']) || ''),
    slotIndex: Math.max(0, Math.floor(finiteNumber(
      firstValue(entry, ['slotIndex', 'slot']),
      index,
    ))),
    itemId: String(itemId),
    itemInstanceId: String(firstValue(entry, ['itemInstanceId']) || ''),
    quantity,
  };
}

export function normalizeBaseCampStorage(storage) {
  const entriesPayload = firstValue(storage, ['entries', 'items']);
  const entries = (Array.isArray(entriesPayload) ? entriesPayload : [])
    .map(normalizeBaseCampStorageEntry)
    .filter(Boolean);
  return {
    unlocked: Boolean(firstValue(storage, [
      'unlocked',
      'isUnlocked',
      'available',
    ])),
    capacity: Math.max(
      entries.length,
      Math.floor(nonNegativeNumber(firstValue(storage, ['capacity', 'slotCount']), 20)),
    ),
    revision: nonNegativeNumber(firstValue(storage, ['revision'])),
    entries,
  };
}

export function normalizeBaseCampProfession(profession) {
  if (!profession) return null;
  const professionKey = firstValue(profession, [
    'professionKey',
    'key',
    'id',
  ]);
  if (!professionKey) return null;
  const isActive = firstValue(profession, ['isActive', 'active']);

  const nextLevelExperience = firstValue(profession, [
    'nextLevelExperience',
  ]);

  return {
    professionKey: String(professionKey),
    name: String(
      firstValue(profession, ['name', 'displayName'])
      || professionDisplayName(professionKey),
    ),
    description: String(
      firstValue(profession, ['description'])
      || professionDescription(professionKey),
    ),
    isActive: isActive === undefined ? true : Boolean(isActive),
    sortOrder: finiteNumber(firstValue(profession, ['sortOrder', 'order'])),
    level: Math.max(
      1,
      Math.floor(nonNegativeNumber(firstValue(profession, ['level']), 1)),
    ),
    experience: nonNegativeNumber(firstValue(profession, [
      'experience',
      'xp',
    ])),
    levelStartExperience: nonNegativeNumber(firstValue(profession, [
      'levelStartExperience',
    ])),
    nextLevelExperience: nextLevelExperience == null
      ? null
      : nonNegativeNumber(nextLevelExperience),
  };
}

export function normalizeBaseCampRoute(route) {
  if (!route) return null;
  const regionId = firstValue(route, ['regionId', 'id']);
  if (!regionId) return null;
  return {
    regionId: String(regionId),
    regionName: String(firstValue(route, ['regionName', 'name']) || regionId),
    regionLevel: Math.max(0, Math.floor(nonNegativeNumber(
      firstValue(route, ['regionLevel', 'level']),
    ))),
    installed: Boolean(firstValue(route, ['installed', 'isInstalled'])),
    isCurrentRegion: Boolean(firstValue(route, [
      'isCurrentRegion',
      'currentRegion',
    ])),
    costItemId: String(firstValue(route, ['costItemId', 'itemId']) || ''),
    costItemName: String(firstValue(route, [
      'costItemName',
      'itemName',
    ]) || ''),
    requiredQuantity: Math.floor(nonNegativeNumber(firstValue(route, [
      'requiredQuantity',
      'quantity',
    ]))),
    availableInPack: Math.floor(nonNegativeNumber(firstValue(route, [
      'availableInPack',
      'ownedQuantity',
    ]))),
  };
}

export function normalizeBaseCamp(payload) {
  if (!payload) return null;
  const source = firstValue(payload, ['baseCamp', 'camp']) ?? payload;
  const buildingsPayload = firstValue(source, [
    'buildings',
    'buildingProjects',
    'buildingSlots',
  ]);
  const storagePayload = firstValue(source, ['storage', 'vault', 'chest']) || {};
  const knownRecipeIdsPayload = firstValue(source, [
    'knownRecipeIds',
    'recipes',
  ]);
  const salvageRecipeProgressPayload = firstValue(source, [
    'salvageRecipeProgress',
  ]);
  const salvageSkillPayload = firstValue(source, ['salvageSkill']) || {};
  const professionsPayload = firstValue(source, ['professions']);
  const craftingQueuePayload = firstValue(source, ['craftingQueue']);
  const routesPayload = firstValue(source, ['routes']);
  const inventoryPayload = firstValue(source, ['inventory']);
  const generatedAtValue = firstValue(source, ['generatedAtMs', 'serverTimeMs'])
    ?? firstValue(payload, ['generatedAtMs', 'serverTimeMs']);
  const generatedAtMs = Number(generatedAtValue);

  return {
    baseId: String(firstValue(source, ['baseId', 'id']) || ''),
    isInCamp: Boolean(firstValue(source, [
      'isInCamp',
      'inCamp',
      'canUseCamp',
    ])),
    respawnRegionId: String(firstValue(source, [
      'respawnRegionId',
    ]) || ''),
    isRespawnPoint: Boolean(firstValue(source, [
      'isRespawnPoint',
    ])),
    worldInstanceId: String(firstValue(source, [
      'worldInstanceId',
      'instanceKey',
    ]) || ''),
    generatedAtMs: Number.isFinite(generatedAtMs) ? generatedAtMs : null,
    serverTimeOffsetMs: Number.isFinite(generatedAtMs)
      ? generatedAtMs - Date.now()
      : 0,
    buildings: (Array.isArray(buildingsPayload) ? buildingsPayload : [])
      .map(normalizeBaseCampBuilding)
      .filter(Boolean)
      .sort((left, right) => (
        left.order - right.order
        || left.name.localeCompare(right.name)
      )),
    storage: normalizeBaseCampStorage(storagePayload),
    inventory: Array.isArray(inventoryPayload)
      ? inventoryPayload.map(item => ({
        itemId: String(firstValue(item, ['itemId']) || ''),
        itemInstanceId: String(firstValue(item, ['itemInstanceId']) || ''),
        quantity: Math.floor(nonNegativeNumber(firstValue(item, ['quantity']))),
        x: Math.floor(nonNegativeNumber(firstValue(item, ['x']))),
        y: Math.floor(nonNegativeNumber(firstValue(item, ['y']))),
      }))
      : null,
    knownRecipeIds: Array.from(new Set(
      (Array.isArray(knownRecipeIdsPayload) ? knownRecipeIdsPayload : [])
        .map(recipeId => String(recipeId || '').trim())
        .filter(Boolean),
    )),
    salvageRecipeProgress: Object.values(
      (Array.isArray(salvageRecipeProgressPayload)
        ? salvageRecipeProgressPayload
        : []).reduce((progress, entry) => {
        const itemId = String(firstValue(entry, ['itemId']) || '').trim();
        const salvageCount = Math.floor(nonNegativeNumber(
          firstValue(entry, ['salvageCount']),
        ));
        if (itemId && salvageCount > (progress[itemId]?.salvageCount || 0)) {
          progress[itemId] = { itemId, salvageCount };
        }
        return progress;
      }, {}),
    ),
    salvageSkill: {
      experience: nonNegativeNumber(firstValue(salvageSkillPayload, [
        'experience',
      ])),
      level: Math.max(1, Math.floor(nonNegativeNumber(firstValue(
        salvageSkillPayload,
        ['level'],
      ), 1))),
      nextLevelExperience: firstValue(
        salvageSkillPayload,
        ['nextLevelExperience'],
      ) == null
        ? null
        : nonNegativeNumber(firstValue(
          salvageSkillPayload,
          ['nextLevelExperience'],
        )),
      maxLevel: Math.max(1, Math.floor(nonNegativeNumber(firstValue(
        salvageSkillPayload,
        ['maxLevel'],
      ), 6))),
    },
    professions: (
      Array.isArray(professionsPayload) ? professionsPayload : []
    )
      .map(normalizeBaseCampProfession)
      .filter(Boolean)
      .sort((left, right) => (
        left.sortOrder - right.sortOrder
        || left.name.localeCompare(right.name)
      )),
    craftingQueue: (
      Array.isArray(craftingQueuePayload) ? craftingQueuePayload : []
    ).map(job => ({
      operationId: String(firstValue(job, ['operationId']) || ''),
      itemId: String(firstValue(job, ['itemId']) || ''),
      rarityId: String(firstValue(job, ['rarityId']) || ''),
      craftingStationKey: String(firstValue(job, ['craftingStationKey']) || ''),
      startedAtUtc: firstValue(job, ['startedAtUtc']) || null,
      completesAtUtc: firstValue(job, ['completesAtUtc']) || null,
      ready: Boolean(firstValue(job, ['ready'])),
    })).filter(job => job.itemId),
    routes: (Array.isArray(routesPayload) ? routesPayload : [])
      .map(normalizeBaseCampRoute)
      .filter(Boolean)
      .sort((left, right) => (
        left.regionLevel - right.regionLevel
        || left.regionName.localeCompare(right.regionName)
      )),
  };
}

export function baseCampFromMutation(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const nested = firstValue(payload, ['baseCamp', 'camp', 'snapshot']);
  if (nested) return normalizeBaseCamp(nested);
  const isSnapshot = [
    'baseId',
    'BaseId',
    'buildings',
    'Buildings',
    'storage',
    'Storage',
  ].some(key => Object.prototype.hasOwnProperty.call(payload, key));
  return isSnapshot ? normalizeBaseCamp(payload) : null;
}

export function getBuildingProgress(building, nowMs = Date.now()) {
  if (building?.status === 'completed') return 100;
  if (building?.status !== 'building') return 0;
  const startedAtMs = Date.parse(building.startedAtUtc);
  const completesAtMs = Date.parse(building.completesAtUtc);
  if (!Number.isFinite(completesAtMs)) return 0;
  if (!Number.isFinite(startedAtMs) || completesAtMs <= startedAtMs) {
    return nowMs >= completesAtMs ? 100 : 0;
  }
  return Math.min(100, Math.max(
    0,
    ((nowMs - startedAtMs) / (completesAtMs - startedAtMs)) * 100,
  ));
}

export function formatBuildingRemaining(building, nowMs = Date.now()) {
  if (building?.status === 'completed') return 'Completed';
  const completesAtMs = Date.parse(building?.completesAtUtc);
  if (!Number.isFinite(completesAtMs)) return 'Build time unavailable';
  const seconds = Math.max(0, Math.ceil((completesAtMs - nowMs) / 1000));
  if (seconds === 0) return 'Completion pending server confirmation';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s remaining`;
  return `${remainingSeconds}s remaining`;
}

const BASE_CAMP_ERROR_MESSAGES = {
  BUILD_ALREADY_ACTIVE: 'Another building project is already underway.',
  ANOTHER_BUILDING_IN_PROGRESS: 'Another building project is already underway.',
  BUILDING_ALREADY_COMPLETED: 'That building is already complete.',
  BUILDING_NOT_FUNDED: 'Deposit every required material before starting construction.',
  MISSING_MATERIALS: 'Deposit every required material before starting construction.',
  INVENTORY_FULL: 'Your Pack has no room for that item.',
  STORAGE_FULL: 'Your Vault has no free slot for that item.',
  STORAGE_NOT_BUILT: 'Build the Found Vault before transferring items.',
  STORAGE_LOCKED: 'Build the Found Vault before transferring items.',
  NOT_IN_CAMP: 'Return to your Camp before using this service.',
  ROUTE_ALREADY_INSTALLED: 'This Camp cableway is already installed.',
  ROUTE_UNAVAILABLE: 'Move next to this region’s cableway anchor.',
};

export function baseCampErrorMessage(error, fallback) {
  const code = String(
    error?.details?.code
    ?? error?.details?.Code
    ?? error?.code
    ?? error?.Code
    ?? '',
  ).toUpperCase();
  return BASE_CAMP_ERROR_MESSAGES[code]
    || error?.message
    || fallback;
}
